import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, FileSpreadsheet, Loader2, CheckCircle, XCircle, Plus } from 'lucide-react';
import { 
  exportProducts, 
  exportQuestions, 
  exportLeads, 
  exportSubmissions,
  exportUploads,
  exportComprehensiveReport
} from '@/lib/excel-export';
import { addSampleSubmissions, addSampleFileUploads, addSampleAnalyticsData } from '@/lib/sample-data';

interface ExportStatus {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
}

export function ExcelExportPanel() {
  const [exportStatuses, setExportStatuses] = useState<Record<string, ExportStatus>>({
    products: { status: 'idle' },
    questions: { status: 'idle' },
    leads: { status: 'idle' },
    submissions: { status: 'idle' },
    uploads: { status: 'idle' },
    comprehensive: { status: 'idle' },
    sampleData: { status: 'idle' },
    sampleFiles: { status: 'idle' },
    sampleAnalytics: { status: 'idle' }
  });

  const updateStatus = (key: string, status: ExportStatus) => {
    setExportStatuses(prev => ({ ...prev, [key]: status }));
  };

  const handleExport = async (exportType: string, exportFunction: () => Promise<{ success: boolean; message?: string; error?: string }>) => {
    updateStatus(exportType, { status: 'loading' });
    
    try {
      const result = await exportFunction();
      if (result.success) {
        updateStatus(exportType, { status: 'success', message: result.message });
        // Reset to idle after 3 seconds
        setTimeout(() => {
          updateStatus(exportType, { status: 'idle' });
        }, 3000);
      } else {
        updateStatus(exportType, { status: 'error', message: result.error });
      }
    } catch (error) {
      updateStatus(exportType, { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  };

  const getStatusIcon = (status: ExportStatus) => {
    switch (status.status) {
      case 'loading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileSpreadsheet className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: ExportStatus) => {
    switch (status.status) {
      case 'loading':
        return <Badge variant="secondary">Exporting...</Badge>;
      case 'success':
        return <Badge variant="default" className="bg-green-500">Downloaded</Badge>;
      case 'error':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Ready</Badge>;
    }
  };

  const exportOptions = [
    {
      key: 'products',
      title: 'Products Export',
      description: 'Export all insurance products',
      function: exportProducts,
      filename: 'products_YYYY-MM-DD.xlsx'
    },
    {
      key: 'questions',
      title: 'Questions Export',
      description: 'Export all questions with product details',
      function: exportQuestions,
      filename: 'questions_YYYY-MM-DD.xlsx'
    },
    {
      key: 'leads',
      title: 'Leads Export',
      description: 'Export leads with contact information',
      function: exportLeads,
      filename: 'leads_YYYY-MM-DD.xlsx'
    },
    {
      key: 'submissions',
      title: 'Submissions Export',
      description: 'Export form submissions with answers',
      function: exportSubmissions,
      filename: 'submissions_YYYY-MM-DD.xlsx'
    },
    {
      key: 'uploads',
      title: 'File Uploads Export',
      description: 'Export uploaded files metadata',
      function: exportUploads,
      filename: 'file_uploads_YYYY-MM-DD.xlsx'
    },
    {
      key: 'comprehensive',
      title: 'Comprehensive Report',
      description: 'Export all data in multiple sheets',
      function: exportComprehensiveReport,
      filename: 'business_insurance_report_YYYY-MM-DD.xlsx'
    }
  ];

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Excel Export Panel
        </CardTitle>
        <CardDescription>
          Export your business insurance data to Excel files for analysis and reporting
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {exportOptions.map((option) => {
          const status = exportStatuses[option.key];
          return (
            <div key={option.key} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(status)}
                <div className="flex-1">
                  <h3 className="font-medium">{option.title}</h3>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                  <p className="text-xs text-gray-500 mt-1">File: {option.filename}</p>
                  {status.message && (
                    <p className={`text-xs mt-1 ${
                      status.status === 'error' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {status.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(status)}
                <Button 
                  onClick={() => handleExport(option.key, option.function)}
                  disabled={status.status === 'loading'}
                  size="sm"
                  variant={option.key === 'comprehensive' ? 'default' : 'outline'}
                >
                  {status.status === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}

        {/* Export Information */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Export Information:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>File Format:</strong> Excel (.xlsx) with proper formatting</p>
            <p><strong>Data Source:</strong> Live data from your Supabase database</p>
            <p><strong>File Naming:</strong> Includes current date for easy organization</p>
            <p><strong>Comprehensive Report:</strong> Contains all data in separate sheets</p>
          </div>
        </div>

        {/* Sample Data Notice */}
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-yellow-900">📊 Current Data Status:</h4>
            <Button
              onClick={() => handleExport('sampleData', addSampleSubmissions)}
              disabled={exportStatuses.sampleData.status === 'loading'}
              size="sm"
              variant="outline"
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              {exportStatuses.sampleData.status === 'loading' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sample Data
                </>
              )}
            </Button>
            <Button
              onClick={() => handleExport('sampleFiles', addSampleFileUploads)}
              disabled={exportStatuses.sampleFiles.status === 'loading'}
              size="sm"
              variant="outline"
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              {exportStatuses.sampleFiles.status === 'loading' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sample Files
                </>
              )}
            </Button>
            <Button
              onClick={() => handleExport('sampleAnalytics', addSampleAnalyticsData)}
              disabled={exportStatuses.sampleAnalytics.status === 'loading'}
              size="sm"
              variant="outline"
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              {exportStatuses.sampleAnalytics.status === 'loading' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Analytics Data
                </>
              )}
            </Button>
          </div>
          <div className="text-sm text-yellow-800 space-y-1">
            <p>• <strong>Products:</strong> 9 insurance products configured</p>
            <p>• <strong>Questions:</strong> 67 questions across all products</p>
            <p>• <strong>Leads:</strong> Sample lead data available</p>
            <p>• <strong>Submissions:</strong> Click "Add Sample Data" to create test submissions</p>
            <p>• <strong>Files:</strong> Click "Add Sample Files" to create test file upload records</p>
            <p>• <strong>Analytics:</strong> Click "Add Analytics Data" to create sample leads for metrics</p>
          </div>
          {exportStatuses.sampleData.message && (
            <p className={`text-sm mt-2 ${
              exportStatuses.sampleData.status === 'error' ? 'text-red-600' : 'text-green-600'
            }`}>
              {exportStatuses.sampleData.message}
            </p>
          )}
          {exportStatuses.sampleFiles.message && (
            <p className={`text-sm mt-1 ${
              exportStatuses.sampleFiles.status === 'error' ? 'text-red-600' : 'text-green-600'
            }`}>
              {exportStatuses.sampleFiles.message}
            </p>
          )}
          {exportStatuses.sampleAnalytics.message && (
            <p className={`text-sm mt-1 ${
              exportStatuses.sampleAnalytics.status === 'error' ? 'text-red-600' : 'text-green-600'
            }`}>
              {exportStatuses.sampleAnalytics.message}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
